import React from 'react';
import { useDisplayMessage } from '../Common/useDisplayMessage';
import EntityModal, { ModalMode } from '../Common/EntityModal';
import { Path, useForm } from 'react-hook-form';
import { RowFormText } from '../Common/Forms/FormText';
import { SupplierDto } from '../Requests/Suppliers/useGetSuppliers';
import useUpsertSupplier, { SupplierUpsertDto } from '../Requests/Suppliers/useUpsertSupplier';
import { FormEmailList, fromEmailFields, toEmailFields } from '../Common/Forms/FormEmailList';

interface IProps {
  initSupplier?: SupplierDto,
  onHide: () => void,
  onSuccess: (supplier: SupplierUpsertDto) => void,
  mode: ModalMode
}

type SupplierForm = Omit<SupplierUpsertDto, 'emailAddresses'> & {
  emailAddresses: { email: string }[]
}

export const CreateEditSupplier = (props: IProps) => {
  const displayMessage = useDisplayMessage();
  let { id, ...initSupplier } = { ...props.initSupplier };

  const { register, handleSubmit, control, getValues, setValue, trigger, formState: { errors } } = useForm<SupplierForm>({
    defaultValues: {
      ...initSupplier,
      emailAddresses: toEmailFields(initSupplier.emailAddresses),
    }
  });

  const handleCreateUpdate = (formValue: SupplierForm) => {
    useCreateEdit.request({
      ...formValue,
      emailAddresses: fromEmailFields(formValue.emailAddresses),
    });
  }

  const useCreateEdit = useUpsertSupplier(props.initSupplier?.id, {
    onSuccess: () => {
      const formValues = getValues();
      props.onSuccess({
        ...formValues,
        emailAddresses: fromEmailFields(formValues.emailAddresses),
      });
    },
    onError: (e) => displayMessage.fail(e.message),
  });

  const hookProps = (name: Path<SupplierForm>) => ({
    labelSpace: 4,
    inputSpace: 7,
    name: name,
    setValue,
    register,
    errors,
  });

  const onClose = () => {
    props.onHide();
    displayMessage.clear();
  }

  return (<EntityModal
    mode={props.mode}
    entityName="supplier"
    displayMessage={displayMessage}
    onHide={onClose}
    isSaving={useCreateEdit.isLoading}
    onSubmit={handleSubmit(handleCreateUpdate)}
    renderForm={(<>
      <RowFormText
        {...hookProps("name")}
        options={{
          required: 'Name is required',
          validate: {
            length: v => v.length < 51 || 'Name cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("accountNumber")}
        options={{
          validate: {
            length: v => v.length < 51 || 'Account Number cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("addressStreet")}
        options={{
          validate: {
            length: v => v.length < 51 || 'Address Street cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("addressCity")}
        options={{
          validate: {
            length: v => v.length < 51 || 'Address City cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("addressState")}
        options={{
          validate: {
            length: v => v.length < 51 || 'Address State cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("addressPostalCode")}
        options={{
          validate: {
            length: v => v.length < 16 || 'Address Postal Code cannot be longer than 15 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("addressCountry")}
        options={{
          validate: {
            length: v => v.length < 101 || 'Address Country cannot be longer than 100 characters'
          }
        }}
      />
      <FormEmailList {...hookProps('emailAddresses')}
        control={control}
        trigger={trigger}
        disabled={props.mode === ModalMode.View}
        max={10} />
    </>)} />);
}
